// Login.tsx - CON OJITO PARA VER CONTRASEÑA
import { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { Eye, EyeOff } from 'lucide-react';
import TwoFactorInput from './TwoFactorInput';
import { scheduleTokenRefresh } from '../services/api';

// Interfaces
interface LoginFormData {
  email: string;
  contrasenia: string;
}

interface GoogleAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: string;
  showRegisterOption?: boolean;
  onRegister?: () => void;
}

// Verificar si Google OAuth está disponible
const isGoogleAuthAvailable = () => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const enabled = process.env.REACT_APP_ENABLE_GOOGLE_AUTH !== 'false';
  return !!(clientId && clientId !== '' && enabled);
};

// Modal mejorado para diferentes escenarios de Google Auth
const GoogleAuthModal: React.FC<GoogleAuthModalProps> = ({ 
  isOpen, 
  onClose, 
  message, 
  showRegisterOption = false,
  onRegister 
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 transform animate-in zoom-in-95 duration-200">
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>
        
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            {showRegisterOption ? 'No tienes cuenta' : 'Método de login diferente'}
          </h2>
          <p className="text-gray-600">
            {message}
          </p>
        </div>
        
        <div className="space-y-4">
          {showRegisterOption && onRegister && (
            <button
              onClick={onRegister}
              className="w-full bg-gradient-to-r from-green-500 to-blue-500 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              Ir a Registro
            </button>
          )}
          
          <button
            onClick={onClose}
            className={`w-full py-4 px-6 rounded-xl font-semibold text-lg hover:scale-105 transition-all duration-300 ${
              showRegisterOption 
                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                : 'bg-gradient-to-r from-blue-500 to-green-500 text-white hover:shadow-lg'
            }`}
          >
            {showRegisterOption ? 'Cerrar' : 'Entendido'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Login = () => {
  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormData>();
  const navigate = useNavigate();

  // Detectar si viene de sesión expirada
  const sessionExpired = new URLSearchParams(window.location.search).get('session_expired') === 'true';

  // Estados
  const [isLoading, setIsLoading] = useState(false);
  const [showGoogleModal, setShowGoogleModal] = useState(false);
  const [googleModalMessage, setGoogleModalMessage] = useState('');
  const [showRegisterOption, setShowRegisterOption] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [sessionExpiredDismissed, setSessionExpiredDismissed] = useState(false);

  // Estados para 2FA
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [twoFactorError, setTwoFactorError] = useState('');
  const [twoFactorUser, setTwoFactorUser] = useState({ email: '', nombre: '' });

  // Verificar disponibilidad de Google Auth
  const googleAuthEnabled = isGoogleAuthAvailable();

  console.log('Login - Google Auth Status:', {
    enabled: googleAuthEnabled,
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID ? 'CONFIGURADO' : 'NO CONFIGURADO',
    envEnabled: process.env.REACT_APP_ENABLE_GOOGLE_AUTH
  });

  // Configuración de la API
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Función auxiliar para guardar datos del usuario
  const saveUserData = (userData: any) => {
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('isLoggedIn', 'true');
    if (userData.token) {
      localStorage.setItem('token', userData.token);
      scheduleTokenRefresh();
    } else {
      console.error('No se recibió token JWT del servidor');
    }
    
    // GUARDAR SUBSCRIPTION DATA SI EXISTE
    if (userData.subscription_status) {
      localStorage.setItem('subscription_status', userData.subscription_status);
    }
    if (userData.trial_ends_at) {
      localStorage.setItem('trial_ends_at', userData.trial_ends_at);
    }
    
    const loginEvent = new Event('userLogin');
    window.dispatchEvent(loginEvent);
  };

  // Función para manejar login exitoso
  const handleSuccessfulLogin = (data: any) => {
    console.log('Login exitoso, datos recibidos:', data);
    
    const userData = {
      id: data.user.id,
      email: data.user.email,
      nombre: data.user.nombre,
      apellido: data.user.apellido,
      es_profesional: data.user.es_profesional,
      professional_type: data.user.professional_type,
      especialidad: data.user.especialidad,
      auth_method: data.user.auth_method || 'manual',
      picture: data.user.picture,
      token: data.token,
      // AGREGAR SUBSCRIPTION DATA
      subscription_status: data.user.subscription_status,
      trial_ends_at: data.user.trial_ends_at,
      // AGREGAR IS_ASSISTANT
      is_assistant: data.user.is_assistant || false
    };

    saveUserData(userData);

    if (userData.es_profesional) {
      console.log('Redirigiendo a dashboard profesional...');
      navigate('/dashboard/profesional', { replace: true });
    } else if (userData.is_assistant) {
      console.log('Redirigiendo a dashboard de asistente...');
      navigate('/assistant/dashboard', { replace: true });
    } else {
      console.log('Redirigiendo a dashboard de paciente...');
      navigate('/dashboard/paciente', { replace: true });
    }
  };

  // Login manual
  const onSubmit: SubmitHandler<LoginFormData> = async (formData) => {
    console.log('Iniciando login manual...', { email: formData.email });
    setIsLoading(true);
    setLoginError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      console.log('Respuesta del servidor:', data);

      if (response.ok && data.success) {
        // Verificar si requiere 2FA
        if (data.requires2FA) {
          setTempToken(data.tempToken);
          setTwoFactorUser({
            email: data.user.email,
            nombre: data.user.nombre
          });
          setShow2FAModal(true);
        } else {
          handleSuccessfulLogin(data);
        }
      } else if (response.status === 400 && data.auth_method === 'google') {
        setGoogleModalMessage(data.message || 'Esta cuenta fue registrada con Google. Por favor usa "Iniciar sesión con Google"');
        setShowRegisterOption(false);
        setShowGoogleModal(true);
      } else {
        setLoginError(data.error || 'Credenciales incorrectas');
      }
    } catch (error) {
      console.error('Error en login:', error);
      setLoginError('Error de conexión. Por favor intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // Google Login
  const handleGoogleSuccess = async (response: CredentialResponse) => {
    if (!googleAuthEnabled) {
      setLoginError('Google OAuth no está configurado correctamente');
      return;
    }

    console.log('Google login iniciado:', response);
    setIsLoading(true);
    setLoginError('');

    try {
      const res = await fetch(`${API_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          token: response.credential,
          isLogin: true
        })
      });
      
      const data = await res.json();
      console.log('Respuesta del servidor Google Auth:', data);

      if (res.ok && data.success) {
        // Verificar si requiere 2FA
        if (data.requires2FA) {
          setTempToken(data.tempToken);
          setTwoFactorUser({
            email: data.user.email,
            nombre: data.user.nombre
          });
          setShow2FAModal(true);
        } else {
          handleSuccessfulLogin(data);
        }
      } else if (res.status === 404 && data.suggest_register) {
        setGoogleModalMessage(data.message || 'No tienes una cuenta con este email. ¿Quieres registrarte?');
        setShowRegisterOption(true);
        setShowGoogleModal(true);
      } else if (res.status === 400) {
        setGoogleModalMessage(data.message || 'Esta cuenta fue registrada con email y contraseña');
        setShowRegisterOption(false);
        setShowGoogleModal(true);
      } else {
        setLoginError(data.error || 'Error en el login con Google');
      }
    } catch (error) {
      console.error('Error en Google login:', error);
      setLoginError('Error de conexión con Google. Por favor intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    console.log('Google login cancelado o falló');
    setLoginError('Login con Google cancelado o falló');
  };

  const handleGoToRegister = () => {
    setShowGoogleModal(false);
    navigate('/register');
  };

  // Manejar verificación 2FA
  const handle2FAVerify = async (code: string) => {
    setIsLoading(true);
    setTwoFactorError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/2fa/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tempToken: tempToken,
          token: code
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setShow2FAModal(false);
        handleSuccessfulLogin(data);
      } else {
        setTwoFactorError(data.error || 'Código incorrecto');
      }
    } catch (error) {
      console.error('Error en verificación 2FA:', error);
      setTwoFactorError('Error de conexión. Intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // Manejar código de respaldo 2FA
  const handle2FABackupCode = async (backupCode: string) => {
    setIsLoading(true);
    setTwoFactorError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/2fa/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tempToken: tempToken,
          backupCode: backupCode
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setShow2FAModal(false);
        handleSuccessfulLogin(data);
        
        // Mostrar advertencia si usó código de respaldo
        if (data.warning) {
          alert(data.warning);
        }
      } else {
        setTwoFactorError(data.error || 'Código de respaldo incorrecto');
      }
    } catch (error) {
      console.error('Error en verificación 2FA con backup:', error);
      setTwoFactorError('Error de conexión. Intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // Cerrar modal 2FA
  const handleClose2FA = () => {
    setShow2FAModal(false);
    setTempToken('');
    setTwoFactorError('');
    setTwoFactorUser({ email: '', nombre: '' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-10 w-20 h-20 bg-blue-500/10 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute top-1/3 right-20 w-32 h-32 bg-green-500/10 rounded-full blur-xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-1/4 left-1/3 w-16 h-16 bg-blue-500/5 rounded-full blur-lg animate-bounce"></div>
      </div>

      <div className="relative max-w-md w-full space-y-8">
        <div className="bg-white p-8 rounded-2xl shadow-2xl border border-gray-100">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="bg-gradient-to-r from-blue-500 to-green-500 p-3 rounded-2xl">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">¡Bienvenido de nuevo!</h2>
            <p className="text-gray-600">Inicia sesión en consultAR</p>
          </div>

          {!googleAuthEnabled && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-yellow-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p className="text-yellow-700 text-sm">
                  <strong>Modo desarrollo:</strong> Google OAuth no configurado. Solo login manual disponible.
                </p>
              </div>
            </div>
          )}

          {/* Aviso de sesión expirada */}
          {sessionExpired && !sessionExpiredDismissed && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-amber-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.832c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <p className="text-amber-700 text-sm font-medium">Tu sesión ha expirado. Por favor, iniciá sesión nuevamente.</p>
                </div>
                <button onClick={() => setSessionExpiredDismissed(true)} className="text-amber-500 hover:text-amber-700 ml-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {loginError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-700 text-sm">{loginError}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email
              </label>
              <input
                {...register('email', { 
                  required: 'El email es requerido',
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: 'Email inválido'
                  }
                })}
                id="email"
                type="email"
                className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
                placeholder="tu@email.com"
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
            </div>

            {/* 👁️ CAMPO DE CONTRASEÑA CON OJITO */}
            <div>
              <label htmlFor="contrasenia" className="block text-sm font-semibold text-gray-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <input
                  {...register('contrasenia', { 
                    required: 'La contraseña es requerida'
                  })}
                  id="contrasenia"
                  type={showPassword ? "text" : "password"}
                  className="w-full p-4 pr-12 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.contrasenia && <p className="text-red-500 text-sm mt-1">{errors.contrasenia.message}</p>}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:shadow-lg hover:scale-105 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Iniciando sesión...</span>
                </>
              ) : (
                <>
                  <span>Iniciar Sesión</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3v1" />
                  </svg>
                </>
              )}
            </button>

            {googleAuthEnabled && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">o inicia sesión con</span>
                  </div>
                </div>

                <div className="text-center">
                  <div className="w-full max-w-[380px] mx-auto">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      theme="outline"
                      size="large"
                      shape="rectangular"
                      width="100%"
                      text="signin_with"
                      locale="es"
                    />
                  </div>
                </div>
              </>
            )}

            <div className="text-center space-y-4">
              <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-700 hover:underline">
                ¿Olvidaste tu contraseña?
              </Link>
              
              <div className="text-gray-600">
                ¿No tienes cuenta?{' '}
                <Link to="/register" className="text-blue-600 hover:text-blue-700 font-semibold hover:underline">
                  Regístrate aquí
                </Link>
              </div>
            </div>
          </form>
        </div>
      </div>

      <GoogleAuthModal
        isOpen={showGoogleModal}
        onClose={() => setShowGoogleModal(false)}
        message={googleModalMessage}
        showRegisterOption={showRegisterOption}
        onRegister={handleGoToRegister}
      />

      {/* Modal 2FA */}
      <TwoFactorInput
        isOpen={show2FAModal}
        onClose={handleClose2FA}
        onVerify={handle2FAVerify}
        onUseBackupCode={handle2FABackupCode}
        isLoading={isLoading}
        error={twoFactorError}
        userEmail={twoFactorUser.email}
        userName={twoFactorUser.nombre}
      />
    </div>
  );
};

export default Login;